import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, GraduationCap, FolderTree, Building2, ClipboardList, ArrowRight } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { LoadingBlock } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";

const COLORS = ["#101876", "#329112", "#f59e0b", "#e11d48", "#8b5cf6", "#06b6d4"];

export default function AdminCourseDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get("/admin/course-dashboard");
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <DashboardLayout title="Course Dashboard"><LoadingBlock label="Loading course dashboard…" /></DashboardLayout>;
  if (!data) return null;
  const t = data.totals;

  return (
    <DashboardLayout title="Course Overview" breadcrumbs={["Platform", "Courses"]}>
      <div className="space-y-6" data-testid="admin-course-dashboard">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-sm text-slate-600">Manage healthcare course catalog across all tenants.</div>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/app/super-admin/courses/categories" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]">
              <FolderTree className="h-3.5 w-3.5" /> Categories
            </Link>
            <Link to="/app/super-admin/courses/new" className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#0c135c]" data-testid="admin-course-create-btn">
              <BookOpen className="h-3.5 w-3.5" /> New course
            </Link>
          </div>
        </div>

        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total Courses" value={t.courses} icon="BookOpen" tone="navy" testId="admin-kpi-courses" index={0} />
          <StatCard label="Published" value={t.published} icon="CircleCheckBig" tone="green" testId="admin-kpi-published" index={1} />
          <StatCard label="Draft" value={t.draft} icon="FileText" tone="slate" testId="admin-kpi-draft" index={2} />
          <StatCard label="Archived" value={t.archived} icon="Archive" tone="slate" testId="admin-kpi-archived" index={3} />
        </section>
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Suspended" value={t.suspended} icon="ShieldOff" tone="amber" testId="admin-kpi-suspended-c" index={4} />
          <StatCard label="Categories" value={t.categories} icon="FolderTree" tone="navy" testId="admin-kpi-categories" index={5} />
          <StatCard label="Orgs with mapping" value={t.orgs_with_mapping} icon="Building2" tone="green" testId="admin-kpi-orgs-mapped" index={6} />
          <StatCard label="Total assignments" value={t.assignments} icon="ClipboardList" tone="slate" testId="admin-kpi-assignments" index={7} />
        </section>

        <section className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Category distribution</div>
            <h3 className="text-lg font-bold text-slate-900">Courses per category</h3>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.per_category}>
                  <XAxis dataKey="category_name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#101876" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Course status</div>
            <h3 className="text-lg font-bold text-slate-900">Status split</h3>
            <div className="mt-2 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.status_stats} dataKey="count" nameKey="status" innerRadius={40} outerRadius={72} paddingAngle={2}>
                    {data.status_stats.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Adoption</div>
            <h3 className="text-lg font-bold text-slate-900">Course mappings per organization</h3>
            <div className="mt-4 space-y-2">
              {data.per_organization.length === 0 && <div className="text-xs text-slate-500">No mappings yet.</div>}
              {data.per_organization.map((r) => (
                <div key={r.organization_id} className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50/60 p-3">
                  <div className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" />{r.organization_name}</div>
                  <div className="text-sm font-black text-[#101876]">{r.count}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Popular</div>
            <h3 className="text-lg font-bold text-slate-900">Top assigned courses</h3>
            <div className="mt-4 space-y-2">
              {data.top_courses.length === 0 && <div className="text-xs text-slate-500">No assignments yet.</div>}
              {data.top_courses.map((r) => (
                <Link key={r.course_id} to={`/app/super-admin/courses/${r.course_id}`} className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50/60 p-3 hover:border-[#101876]/40 hover:bg-white">
                  <div className="text-sm font-semibold text-slate-800 flex items-center gap-2"><GraduationCap className="h-4 w-4 text-slate-400" />{r.course_title}</div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">{r.count} <ArrowRight className="h-3.5 w-3.5 text-slate-400" /></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
